<template>
  <BaseShipmentTimelineItem
    :id="`shipment-service-incident-${shipmentServiceIncident.id}`"
    class="shipment-service-incident p-message"
    :class="incidentSeverity"
    :event-type="shipmentServiceIncident.incidentType"
    :formatted-event-type="serviceIncidentTypeName"
    :subheader="formattedLocation"
    :date-label="t('shipments.dates.actualDateTime')"
    :formatted-date="incidentDateString"
    :menu-items="menuItems"
    :comment="shipmentServiceIncident.comment"
    :disabled="disabled"
    :hide-menu="hideMenu"
  >
    <template #eventTypeIcon>
      <span class="p-message-icon pi" :class="iconType" />
    </template>
  </BaseShipmentTimelineItem>
</template>

<script lang="ts" setup>
import { computed, PropType, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime } from '@rygen/rygen-ui/i18n';
import { RygenMenuItem, ServiceIncidentType } from '@rygen/rygen-ui/model';

import { ShipmentEventSeverity, ShipmentServiceIncident } from '@/model';

import BaseShipmentTimelineItem from '@/components/shipment/BaseShipmentTimelineItem.vue';

const props = defineProps({
  shipmentServiceIncident: {
    type: Object as PropType<ShipmentServiceIncident>,
    required: true,
  },
  serviceIncidentTypes: {
    type: Array as PropType<ServiceIncidentType[]>,
    required: true,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  hideMenu: {
    type: Boolean,
    default: () => false,
  },
});
const { shipmentServiceIncident, serviceIncidentTypes, disabled, hideMenu } =
  toRefs(props);
const emit = defineEmits<{
  (
    event: 'delete-shipment-service-incident',
    serviceIncident: ShipmentServiceIncident,
  ): void;
  (
    event: 'edit-shipment-service-incident',
    serviceIncident: ShipmentServiceIncident,
  ): void;
}>();

const { t } = useI18n();

const formattedLocation = computed((): string => {
  const city = shipmentServiceIncident.value.actualLocation.city;
  const state = shipmentServiceIncident.value.actualLocation.state;

  if (city && state) {
    return `${city}, ${state}`;
  } else if (state) {
    return state;
  } else {
    return t('common.noParam', { param: t('locations.locations') });
  }
});

const incidentDateString = computed(() => {
  const date = shipmentServiceIncident.value.actualDate;
  const timeZone = shipmentServiceIncident.value.actualLocation.timeZone;
  return renderDateTime(date, { separator: '@', timeZone });
});

const incidentSeverity: Ref<string> = computed(() => {
  let severityMessage = 'p-message-info';
  if (
    shipmentServiceIncident.value.severity === ShipmentEventSeverity.WARNING
  ) {
    severityMessage = 'p-message-warn';
  } else if (
    shipmentServiceIncident.value.severity === ShipmentEventSeverity.CRITICAL
  ) {
    severityMessage = 'p-message-error';
  }
  return severityMessage;
});

const iconType: Ref<string> = computed(() => {
  if (incidentSeverity.value === 'p-message-info') {
    return 'pi-info-circle';
  } else if (incidentSeverity.value === 'p-message-warn') {
    return 'pi-exclamation-triangle';
  }
  return 'pi-times-circle';
});

const param = t('events.incidents');
const menuItems: RygenMenuItem[] = [
  {
    label: t('common.editParam', { param }),
    icon: 'pi pi-fw pi-pencil',
    command: () =>
      emit('edit-shipment-service-incident', shipmentServiceIncident.value),
    requiredAuthorization: ['shipmentEvent', 'update'],
  },
  {
    label: t('common.deleteParam', { param }),
    class: 'text-danger',
    icon: 'pi pi-fw pi-trash',
    command: () =>
      emit('delete-shipment-service-incident', shipmentServiceIncident.value),
    requiredAuthorization: ['shipmentEvent', 'delete'],
  },
];

const serviceIncidentTypeName = computed(() => {
  const serviceIncidentType = serviceIncidentTypes.value.find(
    (type) => type.naturalId === shipmentServiceIncident.value.incidentType,
  );

  return (
    serviceIncidentType?.name ??
    shipmentServiceIncident.value.incidentType ??
    ''
  );
});
</script>

<style>
.shipment-service-incident {
  &.p-message-info {
    --base-shipment-timeline-item-header-color: var(--p-message-info-color);
    --base-shipment-timeline-item-subheader-color: var(--p-gray-500);
  }

  &.p-message-warn {
    --base-shipment-timeline-item-header-color: var(--p-message-warn-color);
    --base-shipment-timeline-item-subheader-color: var(--p-gray-500);
  }

  &.p-message-error {
    --base-shipment-timeline-item-header-color: var(--p-message-error-color);
    --base-shipment-timeline-item-subheader-color: var(--p-gray-500);
  }
}
</style>
